import React from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { FiCheck, FiArrowRight, FiAward, FiTool, FiCpu, FiShield } from 'react-icons/fi';
import colors from '../styles/colors';
import usePageMeta from '../hooks/usePageMeta';

const PageWrapper = styled.div`
  padding-top: 80px;
`;

const HeroSection = styled.section`
  padding: 100px 20px;
  background: linear-gradient(135deg, rgba(10, 10, 10, 0.85) 0%, rgba(26, 26, 26, 0.9) 100%),
    url('/images/bmw-front.jpg') center/cover no-repeat;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 50%;
    height: 100%;
    background: linear-gradient(135deg, transparent 0%, ${colors.accent.main}15 100%);
    clip-path: polygon(30% 0, 100% 0, 100% 100%, 0% 100%);
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${colors.accent.gradient};
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const HeroContent = styled.div`
  max-width: 700px;
  position: relative;
  z-index: 1;
`;

const Badge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: ${colors.accent.main}20;
  border: 1px solid ${colors.accent.main}40;
  border-radius: 50px;
  color: ${colors.accent.light};
  font-size: 0.85rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  margin-bottom: 20px;
`;

const Title = styled.h1`
  font-size: clamp(2.5rem, 6vw, 4rem);
  color: ${colors.text.heading};
  margin-bottom: 20px;
  line-height: 1.1;

  span {
    color: ${colors.accent.main};
  }
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: ${colors.text.light};
  line-height: 1.8;
  max-width: 550px;
`;

const Section = styled.section`
  padding: 100px 20px;
  background: ${props => props.$alt ? colors.background.card : colors.background.medium};
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    gap: 40px;
  }
`;

const Content = styled.div``;

const SectionLabel = styled.div`
  font-family: 'Oswald', sans-serif;
  font-size: 0.9rem;
  color: ${colors.accent.main};
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 12px;
`;

const SectionTitle = styled.h2`
  font-size: clamp(2rem, 5vw, 2.8rem);
  color: ${colors.text.heading};
  margin-bottom: 24px;

  &::after {
    content: '';
    display: block;
    width: 80px;
    height: 4px;
    background: ${colors.metallic.silver};
    margin-top: 16px;
    border-radius: 2px;
  }
`;

const Text = styled.div`
  p {
    font-size: 1.05rem;
    color: ${colors.text.light};
    line-height: 1.8;
    margin-bottom: 20px;

    &:last-child {
      margin-bottom: 0;
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  border-radius: 12px;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: -2px;
    left: -2px;
    right: -2px;
    bottom: -2px;
    background: ${colors.metallic.silver};
    border-radius: 14px;
    z-index: -1;
  }

  @media (max-width: 900px) {
    order: -1;
  }
`;

const Image = styled.img`
  width: 100%;
  height: 400px;
  object-fit: cover;
  border-radius: 12px;
  border: 2px solid ${colors.borderLight};
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
  margin-top: 30px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const ServiceItem = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: ${colors.background.card};
  border: 1px solid ${colors.border};
  border-radius: 10px;
  color: ${colors.text.light};
  font-size: 0.95rem;
  transition: all 0.3s ease;

  svg {
    color: ${colors.accent.main};
    flex-shrink: 0;
  }

  &:hover {
    border-color: ${colors.accent.main};
    transform: translateX(5px);
  }
`;

const FeaturesSection = styled.section`
  padding: 100px 20px;
  background: ${colors.background.dark};
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: ${colors.background.card};
  border: 1px solid ${colors.border};
  border-radius: 16px;
  padding: 35px;
  text-align: center;
  transition: all 0.3s ease;

  &:hover {
    border-color: ${colors.accent.main};
    transform: translateY(-5px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
  }
`;

const FeatureIcon = styled.div`
  width: 70px;
  height: 70px;
  background: ${colors.accent.gradient};
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
  box-shadow: 0 10px 30px rgba(0, 102, 177, 0.3);

  svg {
    font-size: 1.8rem;
    color: white;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.2rem;
  color: ${colors.text.heading};
  margin-bottom: 12px;
`;

const FeatureDesc = styled.p`
  font-size: 0.95rem;
  color: ${colors.text.light};
  line-height: 1.7;
`;

const CTASection = styled.section`
  padding: 80px 20px;
  background: linear-gradient(135deg, ${colors.accent.dark} 0%, ${colors.accent.main} 100%);
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2rem;
  color: white;
  margin-bottom: 15px;
`;

const CTAText = styled.p`
  font-size: 1.1rem;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 30px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;
`;

const CTAButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 18px 40px;
  background: white;
  color: ${colors.accent.dark};
  font-family: 'Oswald', sans-serif;
  font-size: 1rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 1px;
  border-radius: 6px;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.3);
  }

  svg {
    transition: transform 0.3s ease;
  }

  &:hover svg {
    transform: translateX(5px);
  }
`;

const bmwServices = [
  'Oil Changes & Fluid Service',
  'Brake Inspection & Repair',
  'Suspension Work',
  'Engine Diagnostics',
  'Electrical Systems',
  'Cooling System Service',
  'Timing Belt/Chain',
  'Pre-Purchase Inspections',
];

export default function BMWPage() {
  usePageMeta(
    'BMW Repair Specialist | Charlotte, VT | The Honest Mechanic',
    'Specialized BMW repair in Charlotte, VT. 8+ years hands-on experience with all BMW models. Oil changes, brakes, suspension, engine diagnostics, electrical, and more.'
  );

  return (
    <PageWrapper>
      <HeroSection>
        <Container>
          <HeroContent>
            <Badge><FiAward /> Our Specialty</Badge>
            <Title>
              <span>BMW</span> Experts
            </Title>
            <Subtitle>
              With 8 years of hands-on BMW experience, we understand the precision engineering
              and give your ultimate driving machine the specialized care it deserves.
            </Subtitle>
          </HeroContent>
        </Container>
      </HeroSection>

      <Section>
        <Container>
          <Grid>
            <Content>
              <SectionLabel>Experience</SectionLabel>
              <SectionTitle>We Know BMWs</SectionTitle>
              <Text>
                <p>
                  With 8 years of hands-on experience working on BMWs, we know
                  these machines inside and out. From routine maintenance to
                  complex repairs, we've got the expertise your BMW deserves.
                </p>
                <p>
                  We understand the precision engineering that goes into every BMW,
                  and we treat each one with the care it requires. Whether you're
                  driving an older E30 or a brand new model, we've got you covered.
                </p>
                <p>
                  While we specialize in BMW, we work on all makes and models.
                  Every car that comes through our shop gets the same honest
                  service and quality work — no surprises, no upsells.
                </p>
              </Text>
            </Content>
            <ImageWrapper>
              <Image src="/images/mechanic-hood.jpg" alt="BMW specialist at work" />
            </ImageWrapper>
          </Grid>
        </Container>
      </Section>

      <Section $alt>
        <Container>
          <SectionLabel>Services</SectionLabel>
          <SectionTitle>BMW Services We Offer</SectionTitle>
          <Text>
            <p>
              From routine maintenance to complex repairs, we provide comprehensive
              service for your BMW. Here's what we can help you with:
            </p>
          </Text>
          <ServicesGrid>
            {bmwServices.map((service, i) => (
              <ServiceItem key={i}>
                <FiCheck /> {service}
              </ServiceItem>
            ))}
          </ServicesGrid>
        </Container>
      </Section>

      <FeaturesSection>
        <Container>
          <FeaturesGrid>
            <FeatureCard>
              <FeatureIcon><FiTool /></FeatureIcon>
              <FeatureTitle>Specialized Tools</FeatureTitle>
              <FeatureDesc>
                We have the BMW-specific tools and equipment needed to properly
                service your vehicle.
              </FeatureDesc>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FiCpu /></FeatureIcon>
              <FeatureTitle>Diagnostic Expertise</FeatureTitle>
              <FeatureDesc>
                Advanced diagnostics to quickly identify issues and get your
                BMW back on the road.
              </FeatureDesc>
            </FeatureCard>
            <FeatureCard>
              <FeatureIcon><FiShield /></FeatureIcon>
              <FeatureTitle>Quality Parts</FeatureTitle>
              <FeatureDesc>
                We use quality OEM and aftermarket parts that meet or exceed
                factory specifications.
              </FeatureDesc>
            </FeatureCard>
          </FeaturesGrid>
        </Container>
      </FeaturesSection>

      <CTASection>
        <Container>
          <CTATitle>Ready to Schedule Service?</CTATitle>
          <CTAText>
            Give us a call or send a message. We'll take care of your BMW like it's our own.
          </CTAText>
          <CTAButton to="/contact">
            Contact Us <FiArrowRight />
          </CTAButton>
        </Container>
      </CTASection>
    </PageWrapper>
  );
}
